// src/pages/LandingSetup.jsx
// Configurazione della "Landing Page Gara": la pagina pubblica /live/current
// quando non c'è una gara attiva (countdown al prossimo evento + circuito).
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './LandingSetup.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

// ISO → valore per <input type="datetime-local"> (ora locale)
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function formatEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const s = new Intl.DateTimeFormat('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Countdown live (per l'anteprima)
function useCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (!iso) return null;
  const target = Date.parse(iso);
  if (!Number.isFinite(target)) return null;
  const diff = target - now;
  if (diff <= 0) return { done: true };
  const sec = Math.floor(diff / 1000);
  return {
    done: false,
    days: Math.floor(sec / 86400),
    hours: Math.floor((sec % 86400) / 3600),
    minutes: Math.floor((sec % 3600) / 60),
    seconds: sec % 60,
  };
}

export default function LandingSetup() {
  const [form, setForm] = useState(null);      // null = loading
  const [circuits, setCircuits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rl, rc] = await Promise.all([
          fetch(`${API_BASE}/api/landing`),
          fetch(`${API_BASE}/api/circuits`),
        ]);
        const jl = rl.ok ? await rl.json() : null;
        const jc = rc.ok ? await rc.json() : [];
        if (!alive) return;
        const l = jl?.landing || {};
        setForm({
          enabled: !!l.enabled,
          eventName: l.eventName || '',
          eventSubtitle: l.eventSubtitle || '',
          eventDateLocal: isoToLocalInput(l.eventDate),
          circuitId: l.circuitId || '',
          location: l.location || '',
        });
        setCircuits(Array.isArray(jc) ? jc : []);
      } catch {
        if (alive) setError('Impossibile caricare la configurazione. Server non raggiungibile.');
      }
    })();
    return () => { alive = false; };
  }, []);

  const set = (k) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setSavedAt(null);
  };

  const eventDateIso = useMemo(() => {
    if (!form?.eventDateLocal) return null;
    const d = new Date(form.eventDateLocal);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }, [form?.eventDateLocal]);

  const cd = useCountdown(eventDateIso);
  const selectedCircuit = useMemo(
    () => circuits.find((c) => c.id === form?.circuitId) || null,
    [circuits, form?.circuitId]
  );

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/landing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: form.enabled,
          eventName: form.eventName,
          eventSubtitle: form.eventSubtitle,
          eventDate: eventDateIso,
          circuitId: form.circuitId,
          location: form.location,
        }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      // Riallinea il form allo stato sanitizzato dal server
      const l = j.landing;
      setForm({
        enabled: !!l.enabled,
        eventName: l.eventName || '',
        eventSubtitle: l.eventSubtitle || '',
        eventDateLocal: isoToLocalInput(l.eventDate),
        circuitId: l.circuitId || '',
        location: l.location || '',
      });
      setSavedAt(Date.now());
    } catch (e) {
      setError(`Salvataggio fallito: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (error && !form) {
    return (
      <div className="main lsg-page"><div className="lsg-error-full">{error}</div></div>
    );
  }
  if (!form) {
    return (
      <div className="main lsg-page"><div className="lsg-loading">Caricamento configurazione...</div></div>
    );
  }

  const prettyDate = formatEventDate(eventDateIso);

  return (
    <div className="main lsg-page">
      {/* Header */}
      <div className="lsg-head">
        <div className="lsg-head-text">
          <span className="lsg-kicker">PAGINA PUBBLICA</span>
          <h1 className="lsg-title">Landing Page Gara</h1>
          <p className="lsg-sub">
            Personalizza cosa vedono gli spettatori su <code>/live/current</code> quando
            non c'è una gara in corso: countdown al prossimo evento e anteprima del circuito.
          </p>
        </div>
        <div className="lsg-head-actions">
          <span className={`lsg-status ${form.enabled ? 'on' : 'off'}`}>
            <span className="lsg-status-dot" />
            {form.enabled ? 'ATTIVA' : 'DISATTIVATA'}
          </span>
          <a className="lsg-open-btn" href="/live/current" target="_blank" rel="noreferrer">
            Apri anteprima
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
          </a>
        </div>
      </div>

      <div className="lsg-grid">
        {/* ==== FORM ==== */}
        <section className="lsg-card">
          <div className="lsg-card-title">Configurazione</div>

          {/* Toggle */}
          <label className="lsg-toggle-row">
            <div className="lsg-toggle-text">
              <strong>Landing attiva</strong>
              <small>Se disattivata, la pagina mostra il messaggio standard "Nessuna gara in corso"</small>
            </div>
            <span className={`lsg-switch ${form.enabled ? 'on' : ''}`}>
              <input type="checkbox" checked={form.enabled} onChange={set('enabled')} />
              <span className="lsg-switch-knob" />
            </span>
          </label>

          <div className="lsg-field">
            <label>Nome evento</label>
            <input
              type="text" maxLength={90} placeholder="es. Trofeo Città di Ferrara"
              value={form.eventName} onChange={set('eventName')}
            />
          </div>

          <div className="lsg-field">
            <label>Sottotitolo / dettagli</label>
            <input
              type="text" maxLength={140} placeholder="es. Round 3 · Campionato RaceSense 2026"
              value={form.eventSubtitle} onChange={set('eventSubtitle')}
            />
          </div>

          <div className="lsg-field-row">
            <div className="lsg-field">
              <label>Data e ora evento <span className="lsg-hint">(countdown)</span></label>
              <div className="lsg-date-wrap">
                <input type="datetime-local" value={form.eventDateLocal} onChange={set('eventDateLocal')} />
                {form.eventDateLocal && (
                  <button
                    type="button" className="lsg-clear-date" title="Rimuovi data"
                    onClick={() => { setForm((f) => ({ ...f, eventDateLocal: '' })); setSavedAt(null); }}
                  >✕</button>
                )}
              </div>
            </div>
            <div className="lsg-field">
              <label>Località</label>
              <input
                type="text" maxLength={90} placeholder="es. Kartodromo di Ferrara"
                value={form.location} onChange={set('location')}
              />
            </div>
          </div>

          <div className="lsg-field">
            <label>Circuito in anteprima</label>
            <select value={form.circuitId} onChange={set('circuitId')}>
              <option value="">— Nessun circuito —</option>
              {circuits.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.lengthMeters ? ` · ${(c.lengthMeters / 1000).toFixed(2)} km` : ''}
                </option>
              ))}
            </select>
            <small className="lsg-field-note">
              Il tracciato viene disegnato nella landing con cordoli e linea di partenza.
            </small>
          </div>

          <div className="lsg-actions">
            <button className="lsg-save" onClick={save} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva configurazione'}
            </button>
            {savedAt && <span className="lsg-saved">✓ Salvato — le pagine live aperte si aggiornano da sole</span>}
            {error && <span className="lsg-error">{error}</span>}
          </div>
        </section>

        {/* ==== ANTEPRIMA ==== */}
        <section className="lsg-card lsg-preview">
          <div className="lsg-card-title">Anteprima</div>
          {!form.enabled && (
            <div className="lsg-preview-disabled">
              Landing disattivata: su /live/current verrà mostrato il messaggio standard.
            </div>
          )}
          <div className={`lsg-preview-stage ${form.enabled ? '' : 'dimmed'}`}>
            <span className="lsg-preview-kicker">PROSSIMO EVENTO</span>
            <div className="lsg-preview-name">{form.eventName || 'Nome evento'}</div>
            {form.eventSubtitle && <div className="lsg-preview-subtitle">{form.eventSubtitle}</div>}
            {(prettyDate || form.location) && (
              <div className="lsg-preview-meta">
                {prettyDate && <span>📅 {prettyDate}</span>}
                {form.location && <span>📍 {form.location}</span>}
              </div>
            )}
            {cd && !cd.done && (
              <div className="lsg-preview-cd">
                {[['GIORNI', cd.days], ['ORE', cd.hours], ['MIN', cd.minutes], ['SEC', cd.seconds]].map(([lab, val]) => (
                  <div className="lsg-cd-tile" key={lab}>
                    <span className="lsg-cd-num">{String(val).padStart(2, '0')}</span>
                    <span className="lsg-cd-lab">{lab}</span>
                  </div>
                ))}
              </div>
            )}
            {cd?.done && <div className="lsg-preview-live">● EVENTO IN PARTENZA</div>}
            {selectedCircuit && (
              <div className="lsg-preview-circuit">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 12a8 8 0 1 1 16 0v3a3 3 0 0 1-3 3h-2a1 1 0 1 1 0-2h2a1 1 0 0 0 1-1v-3a6 6 0 1 0-12 0v3a1 1 0 0 0 1 1h2a1 1 0 1 1 0 2H7a3 3 0 0 1-3-3v-3z" /></svg>
                {selectedCircuit.name}
                {selectedCircuit.lengthMeters ? <em>{(selectedCircuit.lengthMeters / 1000).toFixed(2)} km</em> : null}
              </div>
            )}
          </div>
          <div className="lsg-preview-note">
            Anteprima indicativa · il tracciato completo con cordoli è visibile su{' '}
            <a href="/live/current" target="_blank" rel="noreferrer">/live/current</a>
          </div>
        </section>
      </div>

      <div className="lsg-back">
        <Link to="/">← Torna alla home</Link>
      </div>
    </div>
  );
}
